import { useEffect, useState } from 'react';
import type { DashboardData, HomeSummary, MeResponse, OutletRef } from '@kassa/shared';
import { api, ApiError } from '../api';
import type { VatAlerts } from '../api/vat';
import { formatMoney, formatQty, hasPermission, kop } from '../format';
import { isoToDmy } from '../dates';
import { InvitationsPanel } from '../components/InvitationsPanel';
import { VerifyEmailNudge } from '../components/EmailVerify';
import { FirstSteps } from '../components/FirstSteps';
import { Icon, type ShellTarget } from '../components/AppShell';
import { VatAlertList } from './Dashboard';

// Ana səhifə (KT-99): «Bu gün» | «Bu ay» yan-yana (telefonda alt-alta) — böyük kart (cəmi satış, çek sayı) və ödəniş
// növləri üzrə kartlar (yalnız obyektdə açıq növlər, KT-95). Altında qısa siyahılar: az qalan mallar, borclular,
// təsdiq gözləyənlər. Sistem vəziyyəti admin panelindədir. Rəqəmlər sənəd və jurnaldan (Bakı tarixi ilə).

interface Props {
  me: MeResponse;
  onNavigate: (to: ShellTarget) => void;
  /** «AI-dan soruş» (KT-100): yalnız ai.ask icazəsi olanda. */
  onAskAi?: (question: string) => void;
  onInvitationAccepted: (me: MeResponse) => void;
}

const IC = {
  receipt: 'M6 3h12v18l-3-2-3 2-3-2-3 2z M9 8h6 M9 12h6 M9 16h3',
  cash: 'M3 7h18v10H3z M12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z M6 10v.01 M18 14v.01',
  card: 'M3 6h18v12H3z M3 10h18 M7 15h4',
  transfer: 'M4 8h13l-3-3 M20 16H7l3 3',
  credit: 'M4 4h12l4 4v12H4z M8 10h8 M8 14h8 M8 18h5',
  bonus: 'M12 3l2.6 5.6 6.1.6-4.6 4.1 1.3 6-5.4-3.1-5.4 3.1 1.3-6-4.6-4.1 6.1-.6z',
  ret: 'M9 14l-5-5 5-5 M4 9h10a6 6 0 0 1 0 12h-3',
  avg: 'M4 19h16 M7 15l4-4 3 3 5-6',
  cancel: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z M5.6 5.6l12.8 12.8',
  box: 'M3 7.5 12 3l9 4.5v9L12 21l-9-4.5z M3 7.5l9 4.5 9-4.5 M12 12v9',
  people: 'M16 20v-1.5a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4V20 M9.5 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z',
  wallet: 'M3 6h18v13H3z M3 10h18 M16 15h2',
  chart: 'M4 20V11 M10 20V5 M16 20v-6 M21 20H3',
  warn: 'M12 4l9 16H3z M12 10v4 M12 17v.01',
  factory: 'M3 21V10l6 3V9l6 3V5h6v16z M7 17h2 M12 17h2 M17 17h1',
};

const WEEKDAY = ['bazar', 'bazar ertəsi', 'çərşənbə axşamı', 'çərşənbə', 'cümə axşamı', 'cümə', 'şənbə'];

function weekday(iso: string): string {
  return WEEKDAY[new Date(`${iso}T12:00:00Z`).getUTCDay()];
}

export function Home({ me, onNavigate, onAskAi, onInvitationAccepted }: Props) {
  const canReport = hasPermission(me, 'report.view');
  const finance = hasPermission(me, 'report.finance');
  const [outlets, setOutlets] = useState<OutletRef[]>([]);
  const [outletId, setOutletId] = useState('');
  const [day, setDay] = useState<HomeSummary | null>(null);
  const [month, setMonth] = useState<HomeSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<VatAlerts | null>(null);
  // «Paneli sazla» (KT-108): gizlədilən kartlar — bu brauzerdə, istifadəçi üzrə
  const hiddenKey = `kassa.home.hidden.${me.tenant?.membershipId ?? ''}`;
  const [hidden, setHidden] = useState<string[]>(() => {
    try {
      const v = JSON.parse(window.localStorage.getItem(hiddenKey) ?? '[]') as unknown;
      return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    } catch {
      return [];
    }
  });
  const [tuning, setTuning] = useState(false);
  const saveHidden = (v: string[]) => {
    setHidden(v);
    try {
      window.localStorage.setItem(hiddenKey, JSON.stringify(v));
    } catch {
      /* yaddaş yoxdur — seçim yalnız bu səhifədə */
    }
  };

  useEffect(() => {
    if (!canReport) return;
    api.outlets().then(setOutlets).catch(() => setOutlets([]));
    if (finance) api.vatAlerts().then(setAlerts).catch(() => setAlerts(null));
  }, [canReport, finance]);

  useEffect(() => {
    if (!canReport) return;
    setError(null);
    const q = { outletId: outletId || undefined };
    Promise.all([api.reportHome({ ...q, period: 'day' }), api.reportHome({ ...q, period: 'month' })])
      .then(([d, m]) => {
        setDay(d);
        setMonth(m);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Göstəricilər yüklənmədi'));
  }, [canReport, outletId]);

  const today = day?.range.from ?? null;

  return (
    <main className="home">
      <div className="home-head">
        <div>
          <h1>Ana səhifə</h1>
          <span className="muted">
            {today ? `${isoToDmy(today)}, ${weekday(today)}` : me.tenant?.tenantName}
            {me.outlet ? ` · ${me.outlet.name}` : ''}
          </span>
        </div>
        <div className="home-head-actions">
        {canReport && (
          <button type="button" className="btn-small" aria-expanded={tuning} onClick={() => setTuning((v) => !v)}>
            Paneli sazla
          </button>
        )}
        {outlets.length > 1 && (
          <select aria-label="Obyekt" className="home-outlet" value={outletId} onChange={(e) => setOutletId(e.target.value)}>
            <option value="">Bütün obyektlər</option>
            {outlets.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        )}
        </div>
      </div>
      {tuning && (
        <fieldset className="card home-tune" aria-label="Paneli sazla">
          <legend>Göstərilən kartlar</legend>
          {KPI_KEYS.map(([k, l]) => (
            <label key={k} className="inline-check">
              <input type="checkbox" checked={!hidden.includes(k)} onChange={(e) => saveHidden(e.target.checked ? hidden.filter((x) => x !== k) : [...hidden, k])} /> {l}
            </label>
          ))}
        </fieldset>
      )}

      <VerifyEmailNudge />
      {me.tenant && hasPermission(me, 'settings.users') && (
        <FirstSteps
          tenantId={me.tenant.tenantId}
          steps={[
            ...(hasPermission(me, 'product.edit') ? [{ label: '1. Malları yükləyin (Excel)', hint: 'Nümunə fayl idxal ekranındadır', onOpen: () => onNavigate('import') }] : []),
            { label: '2. İşçilər və PİN', hint: 'Kassir, anbardar, menecer — hər birinə rol və PİN', onOpen: () => onNavigate('settings') },
            { label: '3. Kassa və cihaz', hint: 'Kassa kompüterini təsdiqləyin (Tənzimləmə → İstifadəçilər → Cihazlar)', onOpen: () => onNavigate('settings') },
            ...(hasPermission(me, 'stock.view') ? [{ label: '4. İlkin qalıq', hint: 'Anbardakı malların sayını daxil edin', onOpen: () => onNavigate('stock') }] : []),
          ]}
        />
      )}
      <InvitationsPanel onAccepted={onInvitationAccepted} />
      {onAskAi && <AskAi onAsk={onAskAi} />}

      {!canReport && (
        <section className="card">
          <h2>{me.tenant?.tenantName ?? 'Şirkət'}</h2>
          <p className="muted">Soldakı menyudan bölmə seçin.</p>
          {hasPermission(me, 'sale.create') && (
            <button type="button" className="btn-primary" onClick={() => onNavigate('sales')}>
              Yeni satış
            </button>
          )}
        </section>
      )}

      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      {alerts && <VatAlertList alerts={alerts} />}
      {canReport && !error && (!day || !month) && <p className="muted">Yüklənir…</p>}

      {day && month && (
        <Dashboard me={me} day={day} month={month} outletId={outletId} hidden={hidden} onNavigate={onNavigate} onOpenStock={() => onNavigate('stock')} />
      )}
    </main>
  );
}

interface Row {
  key: string;
  a: string;
  b: string;
}

function ShortList({ title, tone, icon, count, rows, empty, onOpen }: { title: string; tone: string; icon: string; count: number; rows: Row[]; empty: string; onOpen: (() => void) | null }) {
  return (
    <section className={`home-list sec-${tone}`} aria-label={title}>
      <div className="home-list-head">
        <Icon d={icon} size={18} />
        <h2>{title}</h2>
        <span className="home-list-count">{count}</span>
      </div>
      {rows.length === 0 && <p className="muted small home-list-empty">{empty}</p>}
      {rows.map((r) => (
        <div key={r.key} className="home-list-row">
          <span>{r.a}</span>
          <span className="mono">{r.b}</span>
        </div>
      ))}
      {onOpen && (
        <button type="button" className="home-list-link" onClick={onOpen}>
          Hamısına bax →
        </button>
      )}
    </section>
  );
}

function Pending({ onOpen }: { onOpen: () => void }) {
  const [rows, setRows] = useState<Row[] | null>(null);
  useEffect(() => {
    Promise.all([api.transfersPending().catch(() => []), api.approvalsPending().catch(() => [])])
      .then(([t, a]) =>
        setRows([
          ...t.map((x) => ({ key: `t-${x.docId}`, a: `${x.displayNo} · köçürmə · ${x.customerName}`, b: formatMoney(x.pendingAmount) })),
          ...a.map((x) => ({ key: `a-${x.docId}`, a: `${x.displayNo} · ${x.customerName}`, b: formatMoney(x.total) })),
        ]),
      )
      .catch(() => setRows(null));
  }, []);
  if (!rows) return null;
  return <ShortList title="Təsdiq gözləyir" tone="maliyye" icon={IC.wallet} count={rows.length} rows={rows.slice(0, 4)} empty="Gözləyən sənəd yoxdur." onOpen={onOpen} />;
}

/** Ana səhifədə kiçik «AI-dan soruş» sahəsi: sual köməkçi ekranında açılır və göndərilir. */
function AskAi({ onAsk }: { onAsk: (question: string) => void }) {
  const [q, setQ] = useState('');
  return (
    <form
      className="home-ask"
      role="search"
      aria-label="AI-dan soruş"
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim()) onAsk(q.trim());
      }}
    >
      <input type="text" maxLength={1000} placeholder="AI-dan soruş: «Bu gün nə qədər satmışam?»" aria-label="AI-dan soruş" value={q} onChange={(e) => setQ(e.target.value)} />
      <button type="submit" className="btn-small" disabled={!q.trim()}>
        Soruş
      </button>
    </form>
  );
}

const KPI_KEYS: [string, string][] = [
  ['today', 'Bu gün satış'],
  ['month', 'Bu ay satış'],
  ['profit', 'Bu ay mənfəət'],
  ['low', 'Az qalan mallar'],
  ['debt', 'Müştəri borcu'],
  ['supplier', 'Təchizatçıya borc'],
  ['chart', 'Qrafik (6 ay)'],
  ['pays', 'Ödəniş növləri'],
  ['recent', 'Son satışlar və alışlar'],
  ['lists', 'Siyahılar'],
];

const SALE_STATUS: Record<DashboardData['recentSales'][number]['status'], [string, string]> = {
  paid: ['Ödənildi', 'badge-ok'],
  credit: ['Nisyə', 'badge-warn'],
  cancelled: ['Ləğv', 'badge-bad'],
  return: ['Qaytarma', 'badge-bad'],
};
const BUY_STATUS: Record<DashboardData['recentPurchases'][number]['status'], [string, string]> = {
  posted: ['Təsdiqləndi', 'badge-ok'],
  draft: ['Gözləyir', 'badge-warn'],
  cancelled: ['Ləğv', 'badge-bad'],
};
const MON = ['yan', 'fev', 'mar', 'apr', 'may', 'iyn', 'iyl', 'avq', 'sen', 'okt', 'noy', 'dek'];
const hhmm = (iso: string) => new Date(iso).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Baku' });
const ddmm = (iso: string) => new Date(iso).toLocaleDateString('az-AZ', { day: '2-digit', month: '2-digit', timeZone: 'Asia/Baku' });

/** Ana səhifə gövdəsi (KT-108, maket 25): göstərici kartları, 6 aylıq qrafik, ödəniş növləri, son sənədlər, siyahılar. */
function Dashboard({ me, day, month, outletId, hidden, onNavigate, onOpenStock }: { me: MeResponse; day: HomeSummary; month: HomeSummary; outletId: string; hidden: string[]; onNavigate: (to: ShellTarget) => void; onOpenStock: () => void }) {
  const [dash, setDash] = useState<DashboardData | null>(null);
  const [dashError, setDashError] = useState<string | null>(null);
  const [low, setLow] = useState<{ count: number; rows: Row[] } | null>(null);
  const [payPeriod, setPayPeriod] = useState<'day' | 'month'>('day');
  const show = (k: string) => !hidden.includes(k);
  const fin = month.finance;
  useEffect(() => {
    let live = true; // obyekt tez dəyişəndə köhnə cavab yenisinin üstünə yazılmasın
    setDashError(null);
    api
      .reportDashboard(outletId || undefined)
      .then((d) => live && setDash(d))
      .catch((e: unknown) => live && setDashError(e instanceof ApiError ? e.message : 'Qrafik yüklənmədi'));
    if (hasPermission(me, 'stock.view')) {
      api
        .stock({ low: '1', limit: 5 })
        .then((r) => live && setLow({ count: r.total, rows: r.items.map((i) => ({ key: i.productId, a: i.name, b: `${formatQty(i.qtyPhys)} ${i.unit}` })) }))
        .catch(() => live && setLow(null));
    }
    return () => {
      live = false;
    };
  }, [outletId]);

  const avg = day.counts.sales > 0 ? Math.round((kop(day.sales.net) + kop(day.sales.returns)) / day.counts.sales) / 100 : 0;
  const margin = fin && fin.netExVat > 0 ? ((fin.grossProfit / fin.netExVat) * 100).toFixed(1).replace('.', ',') : null;
  type K = { key: string; label: string; value: string; sub: string; tone: string; icon: string; to: ShellTarget | null };
  const kpis: K[] = ([
    { key: 'today', label: 'Bu gün satış', value: formatMoney(day.sales.net), sub: `${day.counts.sales} çek · orta ${formatMoney(avg)}`, tone: 'satis', icon: IC.receipt, to: 'receipts' as ShellTarget },
    { key: 'month', label: 'Bu ay satış', value: formatMoney(month.sales.net), sub: `${month.counts.sales} çek`, tone: 'satis', icon: IC.avg, to: 'reports' as ShellTarget },
    ...(fin ? [{ key: 'profit', label: 'Bu ay mənfəət', value: formatMoney(fin.grossProfit), sub: margin ? `marja ${margin}% (maya ilə)` : 'maya ilə', tone: 'maliyye', icon: IC.chart, to: 'reports' as ShellTarget }] : []),
    ...(low ? [{ key: 'low', label: 'Az qalan mallar', value: String(low.count), sub: 'minimumdan aşağı', tone: 'anbar', icon: IC.warn, to: 'stock' as ShellTarget }] : []),
    ...(fin ? [{ key: 'debt', label: 'Müştəri borcu', value: formatMoney(fin.customerDebt), sub: `${fin.debtorCount} müştəri`, tone: 'musteri', icon: IC.credit, to: (hasPermission(me, 'customer.view') ? 'customers' : null) as ShellTarget | null }] : []),
    ...(fin ? [{ key: 'supplier', label: 'Təchizatçıya borc', value: formatMoney(fin.supplierDebt), sub: 'alışlar üzrə', tone: 'musteri', icon: IC.factory, to: (hasPermission(me, 'purchase.view') ? 'purchase' : null) as ShellTarget | null }] : []),
  ] as K[]).filter((k) => show(k.key));

  const p = payPeriod === 'day' ? day : month;
  const pays: { label: string; count: string; value: number; bad?: boolean }[] = [];
  if (p.methods.cash || p.sales.cash) pays.push({ label: 'Nağd', count: `${p.counts.cash} ödəniş`, value: p.sales.cash });
  if (p.methods.card || p.sales.card) pays.push({ label: 'Kart', count: `${p.counts.card} ödəniş`, value: p.sales.card });
  if (p.methods.transfer || p.sales.transfer) pays.push({ label: 'Köçürmə', count: `${p.counts.transfer} ödəniş`, value: p.sales.transfer });
  if (p.methods.bonus || p.sales.bonus) pays.push({ label: 'Bonus', count: `${p.counts.bonus} ödəniş`, value: p.sales.bonus });
  if (p.methods.credit || p.sales.credit) pays.push({ label: 'Nisyə', count: `${p.counts.credit} çek`, value: p.sales.credit });
  pays.push({ label: 'Qaytarma', count: `${p.counts.returns} sənəd`, value: -p.sales.returns, bad: true });
  pays.push({ label: 'Ləğv', count: `${p.cancels.count} çek`, value: -p.cancels.amount, bad: true });
  const payMax = Math.max(1, ...pays.map((x) => Math.abs(x.value)));
  const chartMax = Math.max(1, ...(dash?.months ?? []).flatMap((m) => [m.sales, m.purchases, m.profit ?? 0]));

  return (
    <>
      {kpis.length > 0 && (
        <div className="dash-kpis">
          {kpis.map((k) => (
            <button key={k.key} type="button" className={`dash-kpi sec-${k.tone}`} disabled={!k.to} onClick={() => k.to && onNavigate(k.to)}>
              <span className="dash-kpi-tile">
                <Icon d={k.icon} size={18} />
              </span>
              <span className="dash-kpi-label">{k.label}</span>
              <strong className="dash-kpi-value mono">{k.value}</strong>
              <span className="muted small">{k.sub}</span>
            </button>
          ))}
        </div>
      )}
      {dashError && (
        <p className="bad" role="alert">
          {dashError}
        </p>
      )}

      <div className="dash-row">
        {show('chart') && dash && (
          <section className="card dash-chart" aria-label="Satış, alış və mənfəət — son 6 ay">
            <h2>Satış, alış və mənfəət — son 6 ay</h2>
            <div className="dash-legend">
              <span className="dash-dot is-sales" /> Satış <span className="dash-dot is-buy" /> Alış
              {dash.months[0]?.profit !== null && (
                <>
                  <span className="dash-dot is-profit" /> Mənfəət
                </>
              )}
            </div>
            <div className="dash-bars" role="img" aria-label={dash.months.map((m) => `${m.month}: satış ${formatMoney(m.sales)}`).join('; ')}>
              {dash.months.map((m) => (
                <div key={m.month} className="dash-month">
                  <div className="dash-month-bars">
                    <span className="dash-bar is-sales" style={{ height: `${Math.round((Math.max(0, m.sales) / chartMax) * 100)}%` }} title={`Satış ${formatMoney(m.sales)}`} />
                    <span className="dash-bar is-buy" style={{ height: `${Math.round((Math.max(0, m.purchases) / chartMax) * 100)}%` }} title={`Alış ${formatMoney(m.purchases)}`} />
                    {m.profit !== null && <span className="dash-bar is-profit" style={{ height: `${Math.round((Math.max(0, m.profit) / chartMax) * 100)}%` }} title={`Mənfəət ${formatMoney(m.profit)}`} />}
                  </div>
                  <span className="muted small">{MON[Number(m.month.slice(5, 7)) - 1]}</span>
                </div>
              ))}
            </div>
          </section>
        )}
        {show('pays') && (
          <section className="card dash-pays" aria-label="Ödəniş növləri">
            <div className="dash-card-head">
              <h2>Ödəniş növləri</h2>
              <div className="seg" role="group" aria-label="Dövr">
                <button type="button" aria-pressed={payPeriod === 'day'} onClick={() => setPayPeriod('day')}>
                  Bu gün
                </button>
                <button type="button" aria-pressed={payPeriod === 'month'} onClick={() => setPayPeriod('month')}>
                  Bu ay
                </button>
              </div>
            </div>
            <ul className="dash-pay-list">
              {pays.map((x) => (
                <li key={x.label}>
                  <div className="dash-pay-line">
                    <span className={x.bad ? 'bad' : ''}>
                      <strong>{x.label}</strong> <span className="muted small">· {x.count}</span>
                    </span>
                    <span className="mono">{formatMoney(x.value)}</span>
                  </div>
                  <span className="dash-pay-track">
                    <span className={`dash-pay-fill${x.bad ? ' is-bad' : ''}`} style={{ width: `${Math.max(2, Math.round((Math.abs(x.value) / payMax) * 100))}%` }} />
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
      </div>

      {show('recent') && dash && (
        <div className="dash-row">
          <RecentTable
            title="Son satışlar"
            tone="satis"
            icon={IC.receipt}
            cols={['Çek', 'Kassir']}
            rows={dash.recentSales.map((r) => ({ key: r.id, no: r.number, t: hhmm(r.at), who: r.who ?? '—', sum: formatMoney(r.total), st: SALE_STATUS[r.status] }))}
            onAll={() => onNavigate('receipts')}
          />
          {hasPermission(me, 'purchase.view') && (
            <RecentTable
              title="Son alışlar"
              tone="anbar"
              icon={IC.box}
              cols={['Qaimə', 'Təchizatçı']}
              rows={dash.recentPurchases.map((r) => ({ key: r.id, no: r.number, t: ddmm(r.at), who: r.supplier ?? '—', sum: formatMoney(r.total), st: BUY_STATUS[r.status] }))}
              onAll={() => onNavigate('purchase')}
            />
          )}
        </div>
      )}

      {show('lists') && (
        <div className="home-lists">
          {dash && <ShortList title="Ən çox satılan" tone="satis" icon={IC.receipt} count={dash.topProducts.length} rows={dash.topProducts.map((x) => ({ key: x.id, a: x.name, b: formatMoney(x.amount) }))} empty="Bu ay satış yoxdur." onOpen={() => onNavigate('reports')} />}
          {dash && hasPermission(me, 'customer.view') && <ShortList title="Ən yaxşı müştərilər" tone="musteri" icon={IC.people} count={dash.topCustomers.length} rows={dash.topCustomers.map((x) => ({ key: x.id, a: x.name, b: formatMoney(x.amount) }))} empty="Bu ay müştərili satış yoxdur." onOpen={() => onNavigate('customers')} />}
          {low && <ShortList title="Az qalan mallar" tone="anbar" icon={IC.box} count={low.count} rows={low.rows} empty="Minimumdan az qalan mal yoxdur." onOpen={onOpenStock} />}
          {hasPermission(me, 'approve.accounting') && <Pending onOpen={() => onNavigate('transfers')} />}
        </div>
      )}
    </>
  );
}

function RecentTable({ title, tone, icon, cols, rows, onAll }: { title: string; tone: string; icon: string; cols: [string, string]; rows: { key: string; no: string; t: string; who: string; sum: string; st: [string, string] }[]; onAll: () => void }) {
  return (
    <section className={`card dash-recent sec-${tone}`} aria-label={title}>
      <div className="dash-recent-head">
        <Icon d={icon} size={18} />
        <h2>{title}</h2>
        <button type="button" className="dash-recent-all" onClick={onAll}>
          Hamısı
        </button>
      </div>
      {rows.length === 0 ? (
        <p className="muted small home-list-empty">Hələ sənəd yoxdur.</p>
      ) : (
        <div className="table-wrap">
          <table className="data-table" aria-label={title}>
            <thead>
              <tr>
                <th scope="col">{cols[0]}</th>
                <th scope="col">{cols[1]}</th>
                <th scope="col" className="num">
                  Məbləğ
                </th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key}>
                  <td>
                    <span className="mono">{r.no}</span> <span className="muted small">{r.t}</span>
                  </td>
                  <td>{r.who}</td>
                  <td className="num mono">{r.sum}</td>
                  <td>
                    <span className={`badge ${r.st[1]}`}>{r.st[0]}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
